import type { LevelDefinition } from "./types";
import type { WorldDefinition } from "./worldTypes";
import { WorldRegistry } from "./WorldRegistry";
import { WORLD_1_LEVELS } from "./world1";
import { WORLD_2_LEVELS } from "./world2";
import { WORLD_3_LEVELS } from "./world3";
import { WORLD_4_LEVELS } from "./world4";
import { WORLD_5_LEVELS } from "./world5";

/**
 * The full campaign, in play order. Future worlds are added by importing
 * their `worldN.ts` file and appending it here - nothing else in the app
 * (world select, level select, progress storage, continue) needs to
 * change to support more worlds/levels.
 */
const ALL_LEVELS: readonly LevelDefinition[] = [...WORLD_1_LEVELS, ...WORLD_2_LEVELS, ...WORLD_3_LEVELS, ...WORLD_4_LEVELS, ...WORLD_5_LEVELS];

const BY_ID = new Map<string, LevelDefinition>(ALL_LEVELS.map((level) => [level.id, level]));

export interface WorldGroup {
  world: WorldDefinition;
  levels: LevelDefinition[];
}

export const LevelRegistry = {
  all(): readonly LevelDefinition[] {
    return ALL_LEVELS;
  },

  byId(id: string): LevelDefinition | undefined {
    return BY_ID.get(id);
  },

  /** The very first level of the campaign (always unlocked). */
  firstLevel(): LevelDefinition {
    return ALL_LEVELS[0];
  },

  /** The level immediately after `id` in campaign order, if any. */
  nextOf(id: string): LevelDefinition | undefined {
    const level = BY_ID.get(id);
    if (!level) return undefined;
    return ALL_LEVELS.find((l) => l.order === level.order + 1);
  },

  /** Levels grouped by world, in world order - what World/Level Select render. */
  worlds(): WorldGroup[] {
    return WorldRegistry.all().map((world) => ({
      world,
      levels: world.levelIds.map((id) => BY_ID.get(id)).filter((l): l is LevelDefinition => l !== undefined),
    }));
  },

  /** The world group `level` belongs to, if any. */
  groupOf(level: LevelDefinition): WorldGroup | undefined {
    return this.worlds().find((group) => group.world.id === level.worldId);
  },

  /** Whether `level` is the last level (in play order) of its own world. */
  isLastLevelInWorld(level: LevelDefinition): boolean {
    const group = this.groupOf(level);
    if (!group || group.levels.length === 0) return false;
    return group.levels[group.levels.length - 1].id === level.id;
  },
};
